import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Plus, MessageCircle, Clock, Check, X, RefreshCw, Send, User } from "lucide-react";
import { format, parseISO } from "date-fns";

interface CheckInPromptsProps {
  clinicianId: string;
  clients: any[];
}

interface CheckInPrompt {
  id: string;
  client_id: string;
  prompt_text: string;
  prompt_type: string;
  status: string;
  scheduled_date: string;
  scheduled_time: string;
  is_recurring: boolean;
  recurrence_pattern: string;
  client_response: string | null;
  responded_at: string | null;
  created_at: string;
}

const PROMPT_TYPES = [
  { value: "mood_check", label: "Mood Check-In" },
  { value: "homework_reminder", label: "Homework Reminder" },
  { value: "coping_check", label: "Coping Check" },
  { value: "gratitude", label: "Gratitude Prompt" },
  { value: "reflection", label: "Reflection Question" },
  { value: "custom", label: "Custom Message" }
];

const RECURRENCE_PATTERNS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Every 2 weeks" },
  { value: "monthly", label: "Monthly" }
];

const PROMPT_TEMPLATES: Record<string, string[]> = {
  mood_check: [
    "How are you feeling today on a scale of 1-10?",
    "What emotions have been most present for you today?",
    "Take a moment to check in with yourself. How would you describe your current state?"
  ],
  homework_reminder: [
    "Have you had a chance to practice the techniques we discussed?",
    "Reminder: Try to complete your journaling exercise before our next session.",
    "How is the breathing exercise going? Any observations to share?"
  ],
  coping_check: [
    "If you're feeling stressed, remember to use your grounding techniques.",
    "What coping strategies have you used this week?",
    "Take 5 minutes to practice self-care today. You deserve it!"
  ],
  gratitude: [
    "What are three things you're grateful for today?",
    "Name one positive thing that happened this week.",
    "What's something small that brought you joy recently?"
  ],
  reflection: [
    "What insight from our last session has stayed with you?",
    "How have you been applying what we discussed?",
    "What patterns have you noticed in your thoughts this week?"
  ]
};

export function CheckInPrompts({ clinicianId, clients }: CheckInPromptsProps) {
  const { toast } = useToast();
  const [prompts, setPrompts] = useState<CheckInPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [clientFilter, setClientFilter] = useState<string>("all");

  const [newPrompt, setNewPrompt] = useState({
    client_id: "",
    prompt_text: "",
    prompt_type: "mood_check",
    scheduled_date: format(new Date(), "yyyy-MM-dd"),
    scheduled_time: "09:00",
    is_recurring: false,
    recurrence_pattern: "weekly"
  });

  useEffect(() => {
    fetchPrompts();
  }, [clinicianId]);

  const fetchPrompts = async () => {
    const { data, error } = await supabase
      .from("clinician_check_in_prompts")
      .select("*")
      .eq("clinician_id", clinicianId)
      .order("scheduled_date", { ascending: false });

    if (error) {
      toast({ title: "Error", description: "Failed to load check-in prompts", variant: "destructive" });
    } else {
      setPrompts(data || []);
    }
    setLoading(false);
  };

  const handleCreatePrompt = async () => {
    const { error } = await supabase.from("clinician_check_in_prompts").insert({
      clinician_id: clinicianId,
      client_id: newPrompt.client_id,
      prompt_text: newPrompt.prompt_text,
      prompt_type: newPrompt.prompt_type,
      scheduled_date: newPrompt.scheduled_date,
      scheduled_time: newPrompt.scheduled_time,
      is_recurring: newPrompt.is_recurring,
      recurrence_pattern: newPrompt.is_recurring ? newPrompt.recurrence_pattern : null,
      status: "pending"
    });

    if (error) {
      toast({ title: "Error", description: "Failed to create prompt", variant: "destructive" });
    } else {
      toast({ title: "Success", description: "Check-in prompt scheduled" });
      setShowAddDialog(false);
      setNewPrompt({
        client_id: "",
        prompt_text: "",
        prompt_type: "mood_check",
        scheduled_date: format(new Date(), "yyyy-MM-dd"),
        scheduled_time: "09:00",
        is_recurring: false,
        recurrence_pattern: "weekly"
      });
      fetchPrompts();
    }
  };

  const handleCancelPrompt = async (promptId: string) => {
    const { error } = await supabase
      .from("clinician_check_in_prompts")
      .update({ status: "cancelled" })
      .eq("id", promptId);

    if (error) {
      toast({ title: "Error", description: "Failed to cancel prompt", variant: "destructive" });
    } else {
      fetchPrompts();
    }
  };

  const handleSendNow = async (prompt: CheckInPrompt) => {
    // In a real app, this would trigger immediate sending
    toast({ title: "Sent!", description: "Check-in prompt sent to client" });
    await supabase
      .from("clinician_check_in_prompts")
      .update({ status: "sent" })
      .eq("id", prompt.id);
    fetchPrompts();
  };

  const getClientName = (clientId: string) => {
    const client = clients.find(c => c.client_id === clientId);
    return client?.client_profile?.display_name || "Unknown Client";
  };

  const useTemplate = (template: string) => {
    setNewPrompt({ ...newPrompt, prompt_text: template });
  };

  const filteredPrompts = prompts.filter(p => {
    const matchesStatus = statusFilter === "all" || p.status === statusFilter;
    const matchesClient = clientFilter === "all" || p.client_id === clientFilter;
    return matchesStatus && matchesClient;
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/20">Pending</Badge>;
      case "sent":
        return <Badge variant="outline" className="bg-blue-500/10 text-blue-500 border-blue-500/20">Sent</Badge>;
      case "responded":
        return <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/20">Responded</Badge>;
      case "cancelled":
        return <Badge variant="outline" className="bg-muted text-muted-foreground">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="responded">Responded</SelectItem>
            </SelectContent>
          </Select>
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Clients" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Clients</SelectItem>
              {clients.map(client => (
                <SelectItem key={client.id} value={client.client_id}>
                  {client.client_profile?.display_name || 'Client'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              New Check-In
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Schedule Check-In Prompt</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Client</Label>
                <Select value={newPrompt.client_id} onValueChange={(v) => setNewPrompt({ ...newPrompt, client_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(client => (
                      <SelectItem key={client.id} value={client.client_id}>
                        {client.client_profile?.display_name || 'Client'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Prompt Type</Label>
                <Select value={newPrompt.prompt_type} onValueChange={(v) => setNewPrompt({ ...newPrompt, prompt_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROMPT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {newPrompt.prompt_type !== "custom" && PROMPT_TEMPLATES[newPrompt.prompt_type] && (
                <div>
                  <Label className="text-xs text-muted-foreground">Quick Templates</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {PROMPT_TEMPLATES[newPrompt.prompt_type].map((template, i) => (
                      <Button
                        key={i}
                        variant="outline"
                        size="sm"
                        onClick={() => useTemplate(template)}
                        className="text-xs h-auto py-1 px-2"
                      >
                        Template {i + 1}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label>Message</Label>
                <Textarea
                  value={newPrompt.prompt_text}
                  onChange={(e) => setNewPrompt({ ...newPrompt, prompt_text: e.target.value })}
                  placeholder="Enter your check-in message..."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={newPrompt.scheduled_date}
                    onChange={(e) => setNewPrompt({ ...newPrompt, scheduled_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Time</Label>
                  <Input
                    type="time"
                    value={newPrompt.scheduled_time}
                    onChange={(e) => setNewPrompt({ ...newPrompt, scheduled_time: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={newPrompt.is_recurring}
                    onCheckedChange={(checked) => setNewPrompt({ ...newPrompt, is_recurring: checked })}
                  />
                  <Label>Recurring</Label>
                </div>
                {newPrompt.is_recurring && (
                  <Select value={newPrompt.recurrence_pattern} onValueChange={(v) => setNewPrompt({ ...newPrompt, recurrence_pattern: v })}>
                    <SelectTrigger className="w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RECURRENCE_PATTERNS.map(p => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                <Button onClick={handleCreatePrompt} disabled={!newPrompt.client_id || !newPrompt.prompt_text}>
                  Schedule
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Prompts List */}
      {filteredPrompts.length === 0 ? (
        <Card className="bg-card/50 border-border/30">
          <CardContent className="py-12 text-center">
            <MessageCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No Check-In Prompts</h3>
            <p className="text-muted-foreground mb-4">
              Schedule prompts to check in with your clients between sessions
            </p>
            <Button onClick={() => setShowAddDialog(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              Create First Prompt
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredPrompts.map(prompt => (
            <Card key={prompt.id} className="bg-card/50 border-border/30">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="p-2 rounded-full bg-primary/10">
                      <User className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-foreground">{getClientName(prompt.client_id)}</p>
                        {getStatusBadge(prompt.status)}
                        {prompt.is_recurring && (
                          <RefreshCw className="h-3 w-3 text-muted-foreground" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{prompt.prompt_text}</p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {format(parseISO(prompt.scheduled_date), "MMM d, yyyy")} at {prompt.scheduled_time}
                        </span>
                        <span>{PROMPT_TYPES.find(t => t.value === prompt.prompt_type)?.label}</span>
                      </div>
                      
                      {prompt.client_response && (
                        <div className="mt-3 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                          <p className="text-sm font-medium text-green-500 mb-1">Client Response:</p>
                          <p className="text-sm text-foreground">{prompt.client_response}</p>
                          {prompt.responded_at && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {format(parseISO(prompt.responded_at), "MMM d 'at' h:mm a")}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {prompt.status === "pending" && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1"
                        onClick={() => handleSendNow(prompt)}
                      >
                        <Send className="h-3 w-3" />
                        Send Now
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => handleCancelPrompt(prompt.id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
